<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Academia de Golf</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <!-- Tarjetas de Estadísticas -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-4 elevation-1">
              <v-icon size="40" color="primary" class="mb-2">mdi-account-tie</v-icon>
              <div class="display-1 font-weight-bold primary--text">{{ totalTeachers }}</div>
              <div class="caption grey--text">Profesores Activos</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-4 elevation-1">
              <v-icon size="40" color="success" class="mb-2">mdi-calendar-clock</v-icon>
              <div class="display-1 font-weight-bold success--text">{{ totalClinics }}</div>
              <div class="caption grey--text">Clínicas Programadas</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-4 elevation-1">
              <v-icon size="40" color="info" class="mb-2">mdi-account-multiple</v-icon>
              <div class="display-1 font-weight-bold info--text">{{ totalStudents }}</div>
              <div class="caption grey--text">Alumnos Registrados</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-4 elevation-1">
              <v-icon size="40" color="orange" class="mb-2">mdi-calendar-check</v-icon>
              <div class="display-1 font-weight-bold orange--text">{{ upcomingClinics }}</div>
              <div class="caption grey--text">Clínicas Esta Semana</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Próximas Clínicas -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2">mdi-calendar-clock</v-icon>
                Próximas Clínicas
                <v-spacer></v-spacer>
                <v-btn small color="primary" to="/academy/clinics">Ver todas</v-btn>
              </v-card-title>
              <v-data-table
                :headers="upcomingHeaders"
                :items="upcomingClinicsList"
                :loading="false"
                class="elevation-0"
                hide-default-footer
                :no-data-text="'Sin clínicas programadas'">

                <template v-slot:item.date="{ item }">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div class="caption grey--text">{{ item.time }}</div>
                </template>

                <template v-slot:item.teacher="{ item }">
                  <div class="font-weight-bold">{{ item.teacher_name }}</div>
                </template>

                <template v-slot:item.type="{ item }">
                  <v-chip small :color="getClinicTypeColor(item.type)">
                    {{ getClinicTypeLabel(item.type) }}
                  </v-chip>
                </template>

                <template v-slot:item.capacity="{ item }">
                  <div class="text-center">
                    <v-chip small>{{ item.enrolled }}/{{ item.capacity }}</v-chip>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="viewClinicDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- Acceso Rápido -->
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-card link to="/academy/teachers" class="elevation-1 hover-card">
              <v-card-text class="d-flex flex-column align-center justify-center pa-6" style="min-height: 150px;">
                <v-icon size="50" color="primary" class="mb-3">mdi-account-tie</v-icon>
                <h3 class="headline">Profesores</h3>
                <p class="caption grey--text text-center mt-2">Gestiona profesores e instructores</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card link to="/academy/clinics" class="elevation-1 hover-card">
              <v-card-text class="d-flex flex-column align-center justify-center pa-6" style="min-height: 150px;">
                <v-icon size="50" color="success" class="mb-3">mdi-calendar-clock</v-icon>
                <h3 class="headline">Clínicas</h3>
                <p class="caption grey--text text-center mt-2">Programa horarios y clínicas</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card link to="/academy/students" class="elevation-1 hover-card">
              <v-card-text class="d-flex flex-column align-center justify-center pa-6" style="min-height: 150px;">
                <v-icon size="50" color="info" class="mb-3">mdi-account-multiple</v-icon>
                <h3 class="headline">Alumnos</h3>
                <p class="caption grey--text text-center mt-2">Asigna alumnos a clínicas</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-flex>
    </v-layout>

    <!-- Diálogo de detalles de clínica -->
    <v-dialog v-model="clinicDetailDialog" max-width="500px">
      <v-card v-if="selectedClinic">
        <v-card-title>
          <span class="headline">{{ getClinicTypeLabel(selectedClinic.type) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="clinicDetailDialog = false" class="cursor-pointer">mdi-close</v-icon>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Título</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Profesor</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.teacher_name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Fecha</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(selectedClinic.date) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Hora</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.time || '--' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Duración</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.duration_minutes }} minutos</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Nivel</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.level }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Inscritos / Capacidad</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.enrolled }} / {{ selectedClinic.capacity }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Descripción</v-list-item-subtitle>
                <v-list-item-title>{{ selectedClinic.description || '--' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { academyAPI } from '@/services/academy'

export default {
  name: 'AcademyDashboard',
  data () {
    return {
      loading: false,
      upcomingHeaders: [
        { text: 'Fecha/Hora', value: 'date', width: '20%' },
        { text: 'Profesor', value: 'teacher', width: '20%' },
        { text: 'Tipo', value: 'type', width: '20%' },
        { text: 'Inscritos', value: 'capacity', width: '15%' },
        { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
      ],
      clinics: [],
      teachers: [],
      students: [],
      clinicDetailDialog: false,
      selectedClinic: null
    }
  },
  computed: {
    totalTeachers () {
      return this.teachers.length
    },
    totalClinics () {
      return this.clinics.length
    },
    totalStudents () {
      return this.students.length
    },
    upcomingClinics () {
      const today = new Date()
      const todayStr = this.getLocalDateStr(today)
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      const nextWeekStr = this.getLocalDateStr(nextWeek)
      return this.clinics.filter(c => c.date >= todayStr && c.date <= nextWeekStr).length
    },
    upcomingClinicsList () {
      const todayStr = this.getLocalDateStr(new Date())
      return this.clinics
        .filter(c => c.date >= todayStr)
        .sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date)
          return (a.time || '00:00').localeCompare(b.time || '00:00')
        })
        .slice(0, 5)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadTeachers(),
          this.loadClinics(),
          this.loadStudents()
        ])
      } catch (error) {
        console.error('Error loading academy data:', error)
      } finally {
        this.loading = false
      }
    },
    async loadTeachers () {
      try {
        const response = await academyAPI.getTeachers({ is_active: true })
        this.teachers = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        console.error('Error loading teachers:', error)
      }
    },
    async loadClinics () {
      try {
        const response = await academyAPI.getClinics()
        this.clinics = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        console.error('Error loading clinics:', error)
      }
    },
    async loadStudents () {
      try {
        const response = await academyAPI.getStudents({ is_active: true })
        this.students = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        console.error('Error loading students:', error)
      }
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getClinicTypeColor (type) {
      const colors = {
        swing: 'blue',
        putting: 'green',
        pitch: 'orange',
        full_swing: 'purple',
        short_game: 'red'
      }
      return colors[type] || 'grey'
    },
    getClinicTypeLabel (type) {
      const labels = {
        swing: 'Swing',
        putting: 'Putting',
        pitch: 'Pitch',
        full_swing: 'Swing Completo',
        short_game: 'Juego Corto'
      }
      return labels[type] || 'Otro'
    },
    viewClinicDetails (clinic) {
      this.selectedClinic = { ...clinic }
      this.clinicDetailDialog = true
    },
    getLocalDateStr (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.hover-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
}

.hover-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}
</style>
